import { fork, put, select, takeEvery } from 'redux-saga/effects';
import { Action } from 'redux-actions';
import { batchActions } from 'redux-batched-actions';

import { ANALYZE_PURCHASEd_PRODUCTS, FILL_ORDER, setOrder } from './actions';
import { setBreakdownPrices } from '../priceBreakdown/action';
import { PassengersState } from '../passengers/reducers';
import { setPassengersState } from '../passengers/actions';
import { checkoutSelectedServicesStateFactory, parsePriceBreakdown } from '../../utils';
import { PriceBreakdownState } from '../priceBreakdown/reducers';
import { getOrder } from './selectors';
import { Product, ProductType } from '../../../analytics';
import { getAeroexpressProductId } from '../../../Aeroexpress/utils';
import { setOrderPrice } from '../orderPrice/actions';
import { CheckoutState } from '../reducers';
import { SelectedServicesState } from '../selectedServices/reducers';
import { setAllSelectedServices } from '../selectedServices/actions';
import getOrderDataLayer from '../../../DataLayerAnalytics/orderDataLayerInstance';
import { OrderType } from '../../../DataLayerAnalytics/OrderDataLayer/types';
import { setAllEveryPortAction } from '../everyPortServices/actions';
import { EveryPortSelectedService, EveryPortSelectedServices } from '../everyPortServices/reducers';
import { SetEveryTravelCart, setEveryTravelCartIdAction } from '../everyTravel/actions';
import {
	AviaPassengerType,
	AviaTraveller,
	CheckoutOrderFragment,
	FlightSeatMapFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceSeat_Fragment,
	NASProductFragment,
	NASSelectedProductFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderAdditionalServicesFragment,
	OrderPriceQueryQuery
} from '@websky/graphql';

import { createInitialExchangeState } from '@entities/exchange';
import {
	SeatMapPassenger,
	getPassengersInitialState,
	setPassengers,
	setSeatMap,
	setAdditionalServices
} from '@entities/seat-map';
import { getEXSTServices } from '@shared/lib/seat-map/getEXSTServices';
import { createInitialRefundState } from '@entities/refund';
import { setSubsidiesState } from '../subsidies/actions';
import { SubsidiesState } from '../subsidies/reducers';

import { createInitialOrderStateSagas } from '@entities/order';

const getEveryPortState = (selectedServices: NASSelectedProductFragment[]): EveryPortSelectedServices => {
	const servicesByCategory: EveryPortSelectedServices = {};

	selectedServices.forEach(service => {
		const selected: EveryPortSelectedService = {
			product: service.product as NASProductFragment,
			additionalServices: service.additionalService as any,
			fare: service.fare,
			travellerIds: service.travellers.map(traveller => traveller.id),
			status: service.status,
			orders: service.orders
		};

		servicesByCategory[service.product.category] = [
			...(servicesByCategory[service.product.category] ?? []),
			selected
		];
	});

	return servicesByCategory;
};

/**
 * @deprecated Should use DataLayerAnalytics
 */
function* analyzePurchasedProducts() {
	const order: CheckoutOrderFragment = yield select(getOrder);
	const products: Product[] = [];

	order.additionalServices?.insurances?.selectedInsurances?.forEach(({ insuranceProgram }) => {
		products.push({
			id: insuranceProgram.code,
			name: insuranceProgram.name,
			category: ProductType.Insurance,
			price: insuranceProgram?.totalPrice.amount
		});
	});

	order.additionalServices?.transfer?.aeroexpress?.selectedTrips?.forEach(trip => {
		products.push({
			id: getAeroexpressProductId(trip),
			category: ProductType.Transfer
		});
	});

	order.travellers.forEach(({ id: travellerId, services }) => {
		services.gdsServices?.services?.forEach(({ serviceId, count }) => {
			const service = order.additionalServices.gdsServices?.services?.find(({ id }) => id === serviceId);

			if (service) {
				if (service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage) {
					const baggage = service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment;

					products.push({
						id: baggage.rfisc,
						category: ProductType.Baggage,
						name: baggage.name,
						quantity: count,
						variant: baggage.baggageWeight,
						price: baggage.price.amount
					});
				}

				if (service.type === OrderAdditionalServiceGdsServiceServiceType.Meal) {
					const meal = service as GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment;

					products.push({
						id: meal.rfisc,
						category: ProductType.Meal,
						name: meal.name,
						quantity: count,
						price: meal.price.amount
					});
				}
			}
		});

		services.seats?.forEach(({ seat }) => {
			if (!seat.seatService) {
				console.warn(`seat without service passenger ${travellerId}`);

				products.push({
					id: null,
					name: null,
					category: ProductType.Seat,
					variant: seat.number,
					price: seat.price ? seat.price.amount : 0
				});

				return;
			}

			const service = order.additionalServices.gdsServices?.services?.find(
				({ id }) => id === seat.seatService.id
			) as GdsService_OrderAdditionalServiceGdsServiceSeat_Fragment;

			products.push({
				id: service.rfisc,
				name: service.name,
				category: ProductType.Seat,
				variant: seat.number,
				price: seat.price ? seat.price.amount : 0
			});
		});
	});
}

function* fillOrder({ payload: order }: Action<CheckoutOrderFragment>) {
	const actions: Action<
		| PassengersState[]
		| CheckoutOrderFragment
		| PriceBreakdownState
		| CheckoutState['orderPrice']
		| SelectedServicesState
		| EveryPortSelectedService[]
		| SetEveryTravelCart
		| SubsidiesState
	>[] = [];

	if (order.travellers.length) {
		const passengers = order.travellers.map<PassengersState>(passenger => ({
			id: passenger.id,
			type: passenger.type as AviaPassengerType,
			values: passenger.values,
			supplierTravellerType: passenger.supplierTravellerType,
			linkedTraveller: passenger.linkedTraveller as AviaTraveller,
			seatRestrictions: passenger.seatRestrictions
		}));

		actions.push(setPassengersState(passengers));

		const selectedServices = checkoutSelectedServicesStateFactory<CheckoutOrderFragment>(order);

		actions.push(setAllSelectedServices(selectedServices));
	}

	actions.push(setSubsidiesState(order));

	if (order?.price || order?.priceToPay) {
		const orderPrice: OrderPriceQueryQuery['OrderPrice'] = {
			priceToPay: { ...order.priceToPay },
			price: { ...order.price }
		};

		actions.push(setOrderPrice(orderPrice));
	}

	if (order?.additionalServices?.nas?.selectedServices) {
		actions.push(setAllEveryPortAction(getEveryPortState(order.additionalServices.nas.selectedServices)));
	}

	if (order.priceBreakdown) {
		actions.push(setBreakdownPrices(parsePriceBreakdown(order.priceBreakdown)));
	}

	actions.push(
		setEveryTravelCartIdAction({
			cartId: order?.additionalServices?.everyTravel?.initializeParameters?.cartId
		})
	);

	getOrderDataLayer(OrderType.Checkout).setOrder(order);

	actions.push(setOrder(order));
	yield put(batchActions(actions));
}

function* createSeatMapState({ payload: order }: Action<CheckoutOrderFragment>) {
	const extraSeatIds = getEXSTServices(order.additionalServices.gdsServices?.services ?? []).map(
		service => service.id
	);

	const actions: Action<SeatMapPassenger[] | FlightSeatMapFragment | OrderAdditionalServicesFragment | boolean>[] = [
		setPassengers(getPassengersInitialState(order.travellers, extraSeatIds)),
		setSeatMap(order.flight.seatMap),
		setAdditionalServices(order.additionalServices)
	];

	yield put(batchActions(actions));
}

function* fillOrderWorker(action: Action<CheckoutOrderFragment>) {
	yield fork(createSeatMapState, action);
	yield fork(createInitialOrderStateSagas, action);
	yield fork(createInitialExchangeState, action);
	yield fork(createInitialRefundState, action);
	yield fork(fillOrder, action);
}

export function* fillOrderSaga() {
	yield takeEvery(FILL_ORDER, fillOrderWorker);
}

/**
 * @deprecated Should use DataLayerAnalytics
 */
export function* analyzePurchasedProductsSaga() {
	yield takeEvery(ANALYZE_PURCHASEd_PRODUCTS, analyzePurchasedProducts);
}
